import React from 'react';
import { useSelector } from 'react-redux';
import { t } from 'Src/i18n';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { ICONS } from 'Components/utils/icon/icons';
import { getAdditionalTransports } from 'Store/booking/booking.selector';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { useTransportFilters } from 'Src/hooks/use-transport-filters/useTransportFilters';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { Button } from 'Components/frameworks/button/Button';
import { COLORS } from 'Components/utils/color/colors';
import { AdditionalTransportsList } from 'Components/widgets/additional-transports/additional-transports-list/AdditionalTransportsList';
import { AdditionalTransportFilters } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFilters';
import { AdditionalTransportFiltersLayer } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFiltersLayer';
import { AdditionalTransportListHeader } from 'Components/widgets/additional-transports/additional-transports-list/AdditionalTransportListHeader';
import { isTransportTrain } from 'Components/widgets/transport-details/transportUtils';
type AdditionalTransportsProps = {
  proposal: Proposal;
  setSelectedTransport: (transportId: string) => void;
};
export const AdditionalTransports = ({
  proposal,
  setSelectedTransport,
}: AdditionalTransportsProps) => {
  const {
    alternativeTransports,
  }: {
    alternativeTransports: Array<AlternativeTransport>;
  } = useSelector(getAdditionalTransports);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { trackClickEvent } = useTrackEvent();
  const { state, updateFilters, resetFilters } = useTransportFilters(alternativeTransports);
  const { filteredTransports } = state;
  const hasNoAlternativeTransport = alternativeTransports.length === 0;
  const hasOnlyTrain = alternativeTransports.every((transport) =>
    isTransportTrain(transport.inbound, transport.outbound),
  );
  return (
    <div data-testid="AdditionalTransports" className="flex h-auto min-h-full bg-white">
      <div className="hidden w-1/4 border-r-1 border-lightGrey md:block">
        <AdditionalTransportFilters
          hasNoAlternativeTransport={hasNoAlternativeTransport}
          hasOnlyTrain={hasOnlyTrain}
          updateFilters={updateFilters}
          resetFilters={resetFilters}
          state={state}
          proposal={proposal}
        />
      </div>
      <div className="block w-full bg-pearl">
        {filteredTransports.length === 0 ? (
          <div>
            <div className="px-60 py-20">
              <Button
                className="mb-20 block min-w-auto text-16 font-semibold md:hidden"
                color={COLORS.BLACK}
                colorBg={COLORS.WHITE}
                colorBorder={COLORS.BLACK}
                iconRight={{
                  name: ICONS.FILTERS,
                }}
                onClick={() => {
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_TRANSPORT_LAYER, {
                    detail_click: 'advanced_filter_mobile',
                  });
                  onOpen();
                }}
              >
                {t('faceting.filters.filtersButton')}
              </Button>
              <Confirmation
                icon={ICONS.CIRCLED_PLANE}
                status="neutral"
                text={t(
                  'booking.transport.additionalTransports.noAdditionalTransportAvailableDescription',
                )}
                title={t(
                  'booking.transport.additionalTransports.noAdditionalTransportAvailableTitle',
                )}
              />
            </div>
          </div>
        ) : (
          <React.Fragment>
            <AdditionalTransportListHeader
              proposalId={proposal.id}
              totalTransports={filteredTransports.length}
              onOpenLayer={onOpen}
            />
            <AdditionalTransportsList
              proposal={proposal}
              setSelectedTransport={setSelectedTransport}
              alternativeTransports={filteredTransports}
            />
          </React.Fragment>
        )}
      </div>
      {isOpen && (
        <AdditionalTransportFiltersLayer
          hasNoAlternativeTransport={hasNoAlternativeTransport}
          hasOnlyTrain={hasOnlyTrain}
          updateFilters={updateFilters}
          resetFilters={resetFilters}
          state={state}
          proposal={proposal}
          onClose={onClose}
        />
      )}
    </div>
  );
};
